import { useEffect, useRef } from 'react';
import { path } from 'ramda';
import { useGeneralRoutes, useOtherSettings } from '@namespace/cms';
import { useLocation } from '@namespace/i18n';
import { LINE_KEY } from '../../constants';
import { sportsbookActions } from '../../store';
import {
  isDetailsPage,
  isListPage,
  isLiveListPage,
  isPreMatchSportsPage
} from './utils';

const { REMOVE_ALL_LINES, SET_IS_LINE_CACHED } = sportsbookActions;

export const useRemoveLineOnNavigate = () => {
  const routes = useGeneralRoutes();
  const routesRef = useRef(routes);
  const { pathname } = useLocation();
  const prevPathnameRef = useRef<string | undefined>();
  const prePrevPathnameRef = useRef<string | undefined>();
  const otherSettings = useOtherSettings();
  const disableLineCaching = path(
    ['sports', 'disableLineCaching'],
    otherSettings
  );

  useEffect(
    () => () => {
      prePrevPathnameRef.current = prevPathnameRef.current;
      prevPathnameRef.current = pathname;
    },
    [pathname]
  );

  useEffect(() => {
    if (disableLineCaching) {
      return;
    }

    // 0. initial load - no previous pathname -> keep cache
    if (prevPathnameRef.current == null) {
      return;
    }

    // 1. going from list to details -> keep cache
    //  check: prevPathname is list && pathname is details
    if (
      isDetailsPage(pathname, routesRef.current) &&
      isListPage(prevPathnameRef.current, routesRef.current)
    ) {
      SET_IS_LINE_CACHED(true);
      return;
    }

    // 2. going from details to previous list -> keep cache
    //  check: prevPathname is details && pathname === prePrevPathname
    if (
      pathname === prePrevPathnameRef.current &&
      isDetailsPage(prevPathnameRef.current, routesRef.current)
    ) {
      return;
    }

    // x. preserve teasers - if we just change sport
    const exclusions = [];
    if (
      isPreMatchSportsPage(pathname, routesRef.current) &&
      isPreMatchSportsPage(prevPathnameRef.current, routesRef.current)
    ) {
      exclusions.push(LINE_KEY.TEASERS2);
    }

    // x. preserve top live events
    if (
      isLiveListPage(pathname, routesRef.current) &&
      isLiveListPage(prevPathnameRef.current, routesRef.current)
    ) {
      exclusions.push(LINE_KEY.TOP_EVENTS);
    }

    // 3. Clear cache in ALL other cases
    REMOVE_ALL_LINES(exclusions);
  }, [disableLineCaching, pathname]);
};
